import { BackendApiClient } from './backend-client';
import { acceptLanguage } from '@/shared/lib/accept-language';

const backendClient = new BackendApiClient();

export type RoleType = 'open' | 'default' | 'custom' | 'internal' | 'automatic';

export interface Role {
  id: string;
  name: string;
  display_name?: string;
  type?: RoleType;
  [key: string]: any; // Allow for additional properties
}

export interface RoleResponse {
  roles: Role[];
}

// Cache for roles, keyed by the language the backend resolved display_name against.
let rolesCacheByLanguage: Record<string, Role[]> = {};

/**
 * Get all available roles (without scope restrictions)
 * Results are cached per language for performance — cache key is the
 * Accept-Language stored in localStorage by LanguageProvider.
 * @param sessionId - The session ID for authentication
 * @param forceRefresh - If true, bypass cache and fetch fresh data
 * @returns Promise<RoleResponse> - The list of roles
 */
export async function getRoles(
  sessionId: string,
  forceRefresh: boolean = false
): Promise<RoleResponse> {
  try {
    const language = acceptLanguage();
    if (!forceRefresh && rolesCacheByLanguage[language]) {
      return { roles: rolesCacheByLanguage[language] };
    }

    // Call without scope context (no Scope-Id or Scope-Type headers)
    const response = await backendClient.get('/role', sessionId, undefined);

    if (response.success && response.data) {
      const roleResponse = response.data as RoleResponse;
      rolesCacheByLanguage[language] = roleResponse.roles || [];
      return roleResponse;
    } else {
      throw new Error(response.error || 'Failed to fetch roles');
    }
  } catch (error) {
    throw new Error(`Failed to fetch roles: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Clear the roles cache
 */
export function clearRolesCache(): void {
  rolesCacheByLanguage = {};
}

